import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  MessageSquare, Mail, Phone, Send, CreditCard, Clock, 
  CheckCircle, FileText, Key, Loader2, RefreshCw, Save,
  AlertCircle, Info, Edit2, Eye, Copy, X, Code, Sparkles,
  History, Search, Filter, ChevronLeft, ChevronRight, ExternalLink, Settings, Download,
  Shield, Plus, Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Available placeholder variables
const PLACEHOLDERS = [
  { key: '{{customer_name}}', description: 'Customer full name', example: 'Rahul Sharma' },
  { key: '{{customer_phone}}', description: 'Customer phone number', example: '9876543210' },
  { key: '{{vehicle_number}}', description: 'Vehicle registration number', example: 'KA01AB1234' },
  { key: '{{vehicle_make}}', description: 'Vehicle manufacturer', example: 'Maruti Suzuki' },
  { key: '{{vehicle_model}}', description: 'Vehicle model name', example: 'Swift' },
  { key: '{{inspection_date}}', description: 'Scheduled inspection date', example: '25 Mar 2026' },
  { key: '{{inspection_time}}', description: 'Scheduled inspection time', example: '11:00 AM' },
  { key: '{{mechanic_name}}', description: 'Assigned mechanic name', example: 'Sai Bharath' },
  { key: '{{mechanic_phone}}', description: 'Mechanic contact number', example: '9123456789' },
  { key: '{{package_name}}', description: 'Inspection package name', example: 'Comprehensive Inspection' },
  { key: '{{package_price}}', description: 'Package price', example: '₹1,499' },
  { key: '{{payment_link}}', description: 'Payment URL', example: 'https://pay.wisedrive.com/xyz123' },
  { key: '{{report_link}}', description: 'Customer report URL', example: 'https://report.wisedrive.com/abc456' },
  { key: '{{otp}}', description: 'One-time password', example: '123456' },
  { key: '{{order_id}}', description: 'Order/Transaction ID', example: 'ORD-ABC123' },
  { key: '{{overall_rating}}', description: 'Vehicle overall rating', example: '8.5/10' },
  { key: '{{company_name}}', description: 'Company name', example: 'WiseDrive' },
];

// Default templates for each touchpoint and channel
const DEFAULT_TEMPLATES = {
  payment_link: {
    whatsapp: {
      message: `Hi {{customer_name}}! 👋

Thank you for choosing *{{company_name}}* for your vehicle inspection.

🚗 *Vehicle:* {{vehicle_number}}
📦 *Package:* {{package_name}}
💰 *Amount:* {{package_price}}

Please complete your payment using the link below:
{{payment_link}}

For any queries, feel free to reach out!

Best regards,
Team WiseDrive`
    },
    sms: {
      message: `WiseDrive: Hi {{customer_name}}, complete payment of {{package_price}} for {{vehicle_number}} inspection. Pay here: {{payment_link}}`
    },
    email: {
      subject: `Complete Your Payment - {{package_name}} for {{vehicle_number}}`,
      message: `Dear {{customer_name}},

Thank you for choosing WiseDrive for your vehicle inspection.

Vehicle Details:
- Registration: {{vehicle_number}}
- Package: {{package_name}}
- Amount: {{package_price}}

Please click the link below to complete your payment:
{{payment_link}}

If you have any questions, please don't hesitate to contact us.

Best regards,
Team WiseDrive`
    }
  },
  payment_confirmation: {
    whatsapp: {
      message: `✅ *Payment Confirmed!*

Hi {{customer_name}}, we have received your payment.

📋 *Order ID:* {{order_id}}
🚗 *Vehicle:* {{vehicle_number}}
📦 *Package:* {{package_name}}
💰 *Amount:* {{package_price}}

Our team will contact you shortly to schedule your inspection.

Thank you for trusting *WiseDrive*! 🙏`
    },
    sms: {
      message: `WiseDrive: Payment of {{package_price}} received for {{vehicle_number}}. Order ID: {{order_id}}. We'll contact you to schedule inspection.`
    },
    email: {
      subject: `Payment Confirmed - Order {{order_id}}`,
      message: `Dear {{customer_name}},

Your payment has been successfully received!

Order Details:
- Order ID: {{order_id}}
- Vehicle: {{vehicle_number}}
- Package: {{package_name}}
- Amount: {{package_price}}

What's Next?
Our team will contact you within 24 hours to schedule your vehicle inspection at a convenient time.

Thank you for choosing WiseDrive!

Best regards,
Team WiseDrive`
    }
  },
  inspection_scheduled: {
    whatsapp: {
      message: `📅 *Inspection Scheduled!*

Hi {{customer_name}}, your vehicle inspection has been scheduled.

🚗 *Vehicle:* {{vehicle_number}}
📆 *Date:* {{inspection_date}}
⏰ *Time:* {{inspection_time}}
👨‍🔧 *Mechanic:* {{mechanic_name}}
📞 *Contact:* {{mechanic_phone}}

Please ensure your vehicle is available at the scheduled time.

Thank you!
Team *WiseDrive*`
    },
    sms: {
      message: `WiseDrive: Inspection scheduled for {{vehicle_number}} on {{inspection_date}} at {{inspection_time}}. Mechanic: {{mechanic_name}} ({{mechanic_phone}})`
    },
    email: {
      subject: `Inspection Scheduled - {{vehicle_number}} on {{inspection_date}}`,
      message: `Dear {{customer_name}},

Your vehicle inspection has been scheduled!

Inspection Details:
- Vehicle: {{vehicle_number}}
- Date: {{inspection_date}}
- Time: {{inspection_time}}
- Mechanic: {{mechanic_name}}
- Mechanic Contact: {{mechanic_phone}}

Please ensure:
1. Your vehicle is available at the scheduled time
2. All vehicle documents are accessible
3. The vehicle has sufficient fuel

If you need to reschedule, please contact us at least 4 hours before the appointment.

Best regards,
Team WiseDrive`
    }
  },
  inspection_in_progress: {
    whatsapp: {
      message: `🔧 *Inspection Started!*

Hi {{customer_name}}, our mechanic has started inspecting your vehicle.

🚗 *Vehicle:* {{vehicle_number}}
👨‍🔧 *Mechanic:* {{mechanic_name}}

The inspection typically takes 45-60 minutes. We'll notify you once it's complete.

Thank you for your patience!
Team *WiseDrive*`
    },
    sms: {
      message: `WiseDrive: Inspection started for {{vehicle_number}} by {{mechanic_name}}. We'll notify you once complete.`
    },
    email: {
      subject: `Inspection In Progress - {{vehicle_number}}`,
      message: `Dear {{customer_name}},

Good news! Our mechanic has started inspecting your vehicle.

Vehicle: {{vehicle_number}}
Mechanic: {{mechanic_name}}

The inspection typically takes 45-60 minutes. You will receive another notification once the inspection is complete, along with a link to view your detailed report.

Thank you for choosing WiseDrive!

Best regards,
Team WiseDrive`
    }
  },
  inspection_completed: {
    whatsapp: {
      message: `✅ *Inspection Complete!*

Hi {{customer_name}}, your vehicle inspection is now complete.

🚗 *Vehicle:* {{vehicle_number}}
⭐ *Overall Rating:* {{overall_rating}}

📋 View your detailed report here:
{{report_link}}

Thank you for choosing *WiseDrive*! 🙏

If you have any questions about the report, feel free to reach out.`
    },
    sms: {
      message: `WiseDrive: Inspection complete for {{vehicle_number}}. Rating: {{overall_rating}}. View report: {{report_link}}`
    },
    email: {
      subject: `Inspection Complete - {{vehicle_number}} Report Ready`,
      message: `Dear {{customer_name}},

Great news! Your vehicle inspection has been completed.

Vehicle: {{vehicle_number}}
Overall Rating: {{overall_rating}}

Your detailed inspection report is now ready. Click the link below to view:
{{report_link}}

The report includes:
- Comprehensive vehicle condition assessment
- Engine and transmission analysis
- Exterior and interior evaluation
- Recommended repairs (if any)
- Market value insights

If you have any questions about the report, our team is here to help.

Thank you for trusting WiseDrive!

Best regards,
Team WiseDrive`
    }
  },
  report_otp: {
    whatsapp: {
      message: `🔐 *Your OTP for Report Access*

Hi {{customer_name}}, use this OTP to access your vehicle inspection report:

*{{otp}}*

🚗 Vehicle: {{vehicle_number}}

This OTP is valid for 10 minutes.

Team *WiseDrive*`
    },
    sms: {
      message: `WiseDrive: Your OTP to access {{vehicle_number}} inspection report is {{otp}}. Valid for 10 minutes.`
    },
    email: {
      subject: `OTP for Report Access - {{vehicle_number}}`,
      message: `Dear {{customer_name}},

Your One-Time Password (OTP) to access your vehicle inspection report:

OTP: {{otp}}

Vehicle: {{vehicle_number}}

This OTP is valid for 10 minutes. Do not share this OTP with anyone.

If you did not request this OTP, please ignore this email.

Best regards,
Team WiseDrive`
    }
  },
  service_history_payment: {
    whatsapp: {
      message: `Service History Payment Received!\n\nCustomer: {{customer_name}}\nPhone: {{customer_phone}}\nCar: {{car_number}}\nAmount: Rs.{{amount}}\n\nPlease arrange the authorized dealer service history within 1 hour.`
    },
    sms: {
      message: `WiseDrive: Service history payment Rs.{{amount}} received for {{car_number}}. Customer: {{customer_name}} ({{customer_phone}}). Arrange within 1 hour.`
    },
    email: {
      subject: `Service History Payment - {{car_number}} ({{customer_name}})`,
      message: `Service History Payment Received\n\nCustomer: {{customer_name}}\nPhone: {{customer_phone}}\nCar: {{car_number}}\nAmount: Rs.{{amount}}\n\nPlease arrange the authorized dealer service history within 1 hour.\n\n— WiseDrive CRM`
    }
  }
};

// Communication touchpoints definition
const TOUCHPOINTS = [
  {
    id: 'payment_link',
    name: 'Payment Link',
    description: 'Send payment link when customer wants to purchase inspection',
    icon: CreditCard,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50'
  },
  {
    id: 'payment_confirmation',
    name: 'Payment Confirmation',
    description: 'Confirm payment along with inspection package details purchased',
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-50'
  },
  {
    id: 'inspection_scheduled',
    name: 'Inspection Scheduled',
    description: 'Notify when inspection is scheduled with date, time and mechanic details',
    icon: Clock,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50'
  },
  {
    id: 'inspection_in_progress',
    name: 'Inspection In-Progress',
    description: 'Update when mechanic starts the inspection',
    icon: Send,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50'
  },
  {
    id: 'inspection_completed',
    name: 'Inspection Completed',
    description: 'Notify completion along with customer report link',
    icon: FileText,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50'
  },
  {
    id: 'report_otp',
    name: 'Report Access OTP',
    description: 'Send OTP for customer to access their inspection report',
    icon: Key,
    color: 'text-red-600',
    bgColor: 'bg-red-50'
  },
  {
    id: 'service_history_payment',
    name: 'Service History Payment',
    description: 'Notify team when customer pays for service history report',
    icon: Shield,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50'
  }
];

// Communication channels
const CHANNELS = [
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: MessageSquare,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    description: 'WhatsApp Business API'
  },
  {
    id: 'sms',
    name: 'SMS',
    icon: Phone,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    description: 'Fast2SMS / Twilio'
  },
  {
    id: 'email',
    name: 'Email',
    icon: Mail,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    description: 'Email notifications'
  }
];

// Template Editor Modal Component
function TemplateEditorModal({ isOpen, onClose, touchpoint, channel, template, onSave }) {
  const [editedTemplate, setEditedTemplate] = useState(template || {});
  const [activeTab, setActiveTab] = useState('edit');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (template) {
      setEditedTemplate(template);
    }
  }, [template]);

  const insertPlaceholder = (placeholder) => {
    const textarea = document.getElementById('template-message');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = editedTemplate.message || '';
      const newText = text.substring(0, start) + placeholder + text.substring(end);
      setEditedTemplate({ ...editedTemplate, message: newText });
    }
  };

  const getPreviewText = (text) => {
    if (!text) return '';
    let preview = text;
    PLACEHOLDERS.forEach(p => {
      preview = preview.replace(new RegExp(p.key.replace(/[{}]/g, '\\$&'), 'g'), p.example);
    });
    return preview;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(editedTemplate);
      toast.success('Template saved successfully!');
      onClose();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const ChannelIcon = CHANNELS.find(c => c.id === channel)?.icon || MessageSquare;
  const TouchpointIcon = TOUCHPOINTS.find(t => t.id === touchpoint)?.icon || FileText;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <TouchpointIcon className="h-5 w-5 text-gray-600" />
              <span>{TOUCHPOINTS.find(t => t.id === touchpoint)?.name}</span>
            </div>
            <span className="text-gray-400">→</span>
            <div className="flex items-center gap-2">
              <ChannelIcon className={`h-5 w-5 ${CHANNELS.find(c => c.id === channel)?.color}`} />
              <span>{CHANNELS.find(c => c.id === channel)?.name}</span>
            </div>
          </DialogTitle>
          <DialogDescription>
            Edit the message template for this communication touchpoint
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="edit" className="flex items-center gap-2">
              <Edit2 className="h-4 w-4" />
              Edit Template
            </TabsTrigger>
            <TabsTrigger value="preview" className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Preview
            </TabsTrigger>
          </TabsList>

          <TabsContent value="edit" className="flex-1 overflow-auto mt-4">
            <div className="grid grid-cols-3 gap-4">
              {/* Template Editor */}
              <div className="col-span-2 space-y-4">
                {channel === 'email' && (
                  <div className="space-y-2">
                    <Label htmlFor="template-subject">Email Subject</Label>
                    <Input
                      id="template-subject"
                      value={editedTemplate.subject || ''}
                      onChange={(e) => setEditedTemplate({ ...editedTemplate, subject: e.target.value })}
                      placeholder="Enter email subject..."
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="template-message">Message Content</Label>
                  <Textarea
                    id="template-message"
                    value={editedTemplate.message || ''}
                    onChange={(e) => setEditedTemplate({ ...editedTemplate, message: e.target.value })}
                    placeholder="Enter message content..."
                    className="min-h-[300px] font-mono text-sm"
                  />
                </div>
                <div className="text-xs text-gray-500">
                  {channel === 'sms' && (
                    <span>Character count: {(editedTemplate.message || '').length} / 160 (1 SMS credit per 160 chars)</span>
                  )}
                  {channel === 'whatsapp' && (
                    <span>Use *text* for bold, _text_ for italic in WhatsApp</span>
                  )}
                </div>
              </div>

              {/* Placeholder Variables */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Code className="h-4 w-4" />
                  Available Variables
                </Label>
                <ScrollArea className="h-[350px] border rounded-lg p-2">
                  <div className="space-y-1">
                    {PLACEHOLDERS.map((p) => (
                      <button
                        key={p.key}
                        onClick={() => insertPlaceholder(p.key)}
                        className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-100 transition-colors"
                      >
                        <div className="font-mono text-xs text-blue-600">{p.key}</div>
                        <div className="text-xs text-gray-500">{p.description}</div>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="preview" className="flex-1 overflow-auto mt-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Sparkles className="h-4 w-4" />
                Preview with sample data
              </div>
              
              {channel === 'email' && (
                <Card>
                  <CardHeader className="py-3 bg-gray-50 border-b">
                    <div className="text-sm">
                      <div><strong>To:</strong> rahul.sharma@email.com</div>
                      <div><strong>Subject:</strong> {getPreviewText(editedTemplate.subject)}</div>
                    </div>
                  </CardHeader>
                  <CardContent className="py-4">
                    <div className="whitespace-pre-wrap text-sm">
                      {getPreviewText(editedTemplate.message)}
                    </div>
                  </CardContent>
                </Card>
              )}

              {channel === 'whatsapp' && (
                <div className="max-w-md mx-auto">
                  <div className="bg-[#e5ddd5] rounded-lg p-4">
                    <div className="bg-white rounded-lg p-3 shadow-sm">
                      <div className="whitespace-pre-wrap text-sm">
                        {getPreviewText(editedTemplate.message)?.split('*').map((part, i) => 
                          i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                        )}
                      </div>
                      <div className="text-right text-xs text-gray-500 mt-1">
                        10:30 AM ✓✓
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {channel === 'sms' && (
                <div className="max-w-md mx-auto">
                  <Card>
                    <CardHeader className="py-2 bg-blue-600 text-white">
                      <div className="text-sm font-medium">WiseDrive</div>
                    </CardHeader>
                    <CardContent className="py-4">
                      <div className="text-sm">
                        {getPreviewText(editedTemplate.message)}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Communication Logs Component
function CommunicationLogsPanel() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    channel: '',
    status: '',
    event_type: '',
    search: ''
  });
  const [activeStatCard, setActiveStatCard] = useState(null); // Track which stat card is selected
  const [selectedLog, setSelectedLog] = useState(null);
  const [exporting, setExporting] = useState(false);
  const limit = 20;

  // Handle stat card click to filter
  const handleStatCardClick = (cardType) => {
    // Toggle off if already selected
    if (activeStatCard === cardType) {
      setActiveStatCard(null);
      setFilters(prev => ({ ...prev, channel: '', status: '' }));
      setPage(0);
      return;
    }
    
    setActiveStatCard(cardType);
    setPage(0);
    
    switch(cardType) {
      case 'total':
        setFilters(prev => ({ ...prev, channel: '', status: '' }));
        break;
      case 'whatsapp':
        setFilters(prev => ({ ...prev, channel: 'whatsapp', status: '' }));
        break;
      case 'sms':
        setFilters(prev => ({ ...prev, channel: 'sms', status: '' }));
        break;
      case 'email':
        setFilters(prev => ({ ...prev, channel: 'email', status: '' }));
        break;
      case 'failed':
        setFilters(prev => ({ ...prev, channel: '', status: 'failed' }));
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
    fetchErrors();
  }, [page, filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit
      });
      
      if (filters.channel) params.append('channel', filters.channel);
      if (filters.status) params.append('status', filters.status);
      if (filters.event_type) params.append('event_type', filters.event_type);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(`${API_URL}/settings/communication-logs?${params}`);
      setLogs(response.data?.logs || []);
      setTotal(response.data?.total || 0);
    } catch (error) {
      console.error('Failed to fetch communication logs:', error);
      toast.error('Failed to load communication logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/communication-stats`);
      setStats(response.data || {});
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchErrors = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/communication-errors`);
      setErrors(response.data?.errors || []);
    } catch (error) {
      console.error('Failed to fetch errors:', error);
    }
  };

  const exportForDebug = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/settings/communication-logs/export/debug?last_n=100`);
      
      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `communication_logs_debug_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Debug logs exported! Share this file for troubleshooting.');
    } catch (error) {
      console.error('Failed to export:', error);
      toast.error('Failed to export logs');
    } finally {
      setExporting(false);
    }
  };

  const copyLogToClipboard = (log) => {
    const debugInfo = JSON.stringify(log, null, 2);
    navigator.clipboard.writeText(debugInfo);
    toast.success('Log details copied to clipboard!');
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'whatsapp': return <MessageSquare className="h-4 w-4 text-green-600" />;
      case 'email': return <Mail className="h-4 w-4 text-purple-600" />;
      case 'sms': return <Phone className="h-4 w-4 text-blue-600" />;
      default: return <Send className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Success</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700 border-red-200">Failed</Badge>;
      case 'queued':
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Queued</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="communication-logs-panel">
      {/* Stats Cards - Clickable for filtering */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${activeStatCard === 'total' ? 'ring-2 ring-gray-500 bg-gray-50' : ''}`}
          onClick={() => handleStatCardClick('total')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <Send className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.total?.total || 0}</div>
                <div className="text-xs text-gray-500">Total Sent</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${activeStatCard === 'whatsapp' ? 'ring-2 ring-green-500 bg-green-50' : ''}`}
          onClick={() => handleStatCardClick('whatsapp')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.whatsapp?.total || 0}</div>
                <div className="text-xs text-gray-500">WhatsApp</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${activeStatCard === 'sms' ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
          onClick={() => handleStatCardClick('sms')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Phone className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.sms?.total || 0}</div>
                <div className="text-xs text-gray-500">SMS</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${activeStatCard === 'email' ? 'ring-2 ring-purple-500 bg-purple-50' : ''}`}
          onClick={() => handleStatCardClick('email')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Mail className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.email?.total || 0}</div>
                <div className="text-xs text-gray-500">Email</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${activeStatCard === 'failed' ? 'ring-2 ring-red-500 bg-red-50' : ''}`}
          onClick={() => handleStatCardClick('failed')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.total?.failed || 0}</div>
                <div className="text-xs text-gray-500">Failed</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-gray-500 mb-1 block">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by phone, name, email..."
                  value={filters.search}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, search: e.target.value }));
                    setPage(0); // Reset to first page on search
                  }}
                  className="pl-9"
                  data-testid="communication-search-input"
                />
              </div>
            </div>
            <div className="w-[150px]">
              <Label className="text-xs text-gray-500 mb-1 block">Channel</Label>
              <Select value={filters.channel || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, channel: v === "all" ? "" : v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="All Channels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Channels</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[150px]">
              <Label className="text-xs text-gray-500 mb-1 block">Status</Label>
              <Select value={filters.status || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === "all" ? "" : v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="queued">Queued</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[180px]">
              <Label className="text-xs text-gray-500 mb-1 block">Event Type</Label>
              <Select value={filters.event_type || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, event_type: v === "all" ? "" : v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  <SelectItem value="payment_link">Payment Link</SelectItem>
                  <SelectItem value="payment_confirmation">Payment Confirmation</SelectItem>
                  <SelectItem value="inspection_scheduled">Inspection Scheduled</SelectItem>
                  <SelectItem value="inspection_completed">Inspection Completed</SelectItem>
                  <SelectItem value="report_otp">Report OTP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              variant="outline" 
              onClick={() => { 
                setFilters({ channel: '', status: '', event_type: '', search: '' }); 
                setPage(0);
                setActiveStatCard(null); // Reset stat card selection
              }}
            >
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
            <Button variant="outline" onClick={fetchLogs}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              variant="outline" 
              onClick={exportForDebug}
              disabled={exporting}
              className="bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100"
            >
              {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-1" />}
              Export for Debug
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error Summary - Only show if there are errors */}
      {errors.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-red-700 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Recent Errors ({errors.length})
            </CardTitle>
            <CardDescription className="text-red-600">
              Common issues that need attention
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {errors.slice(0, 5).map((error, idx) => (
                <div key={idx} className="bg-white border border-red-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getChannelIcon(error.channel)}
                      <span className="font-mono text-sm text-red-700">{error.error_code || 'Unknown'}</span>
                    </div>
                    <Badge variant="destructive">{error.count}x</Badge>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{error.error_message}</p>
                  <p className="text-xs text-gray-400 mt-1">Last: {formatDate(error.last_occurrence)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <History className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No communication logs found</p>
              <p className="text-sm">Logs will appear here when messages are sent</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Channel</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Event</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Recipient</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Sent By</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {getChannelIcon(log.channel)}
                            <span className="text-sm capitalize">{log.channel}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-700 capitalize">
                            {log.event_type?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-mono text-gray-600">{log.recipient_masked}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-700">{log.customer_name || '-'}</span>
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(log.status)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600">{log.triggered_by_name || 'System'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-500">{formatDate(log.created_at)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setSelectedLog(log)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <div className="text-sm text-gray-500">
                  Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {page + 1} of {totalPages || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Log Detail Modal */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {selectedLog && getChannelIcon(selectedLog.channel)}
                Communication Log Details
              </div>
              {selectedLog && (
                <Button variant="outline" size="sm" onClick={() => copyLogToClipboard(selectedLog)}>
                  <Copy className="h-4 w-4 mr-1" />
                  Copy Debug Info
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <ScrollArea className="max-h-[70vh]">
              <div className="space-y-4 pr-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Channel</Label>
                    <div className="flex items-center gap-2 mt-1">
                      {getChannelIcon(selectedLog.channel)}
                      <span className="capitalize">{selectedLog.channel}</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Status</Label>
                    <div className="mt-1">{getStatusBadge(selectedLog.status)}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Event Type</Label>
                    <div className="mt-1 capitalize">{selectedLog.event_type?.replace(/_/g, ' ')}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Recipient</Label>
                    <div className="mt-1 font-mono text-sm">{selectedLog.recipient}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Customer</Label>
                    <div className="mt-1">{selectedLog.customer_name || '-'}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Triggered By</Label>
                    <div className="mt-1">{selectedLog.triggered_by_name || 'System'}</div>
                  </div>
                </div>

                {selectedLog.message_preview && (
                  <div>
                    <Label className="text-xs text-gray-500">Message Preview</Label>
                    <div className="mt-1 p-3 bg-gray-50 rounded-lg text-sm">
                      {selectedLog.message_preview}
                    </div>
                  </div>
                )}

                {/* Error Section */}
                {selectedLog.status === 'failed' && (
                  <div className="border border-red-200 rounded-lg p-3 bg-red-50">
                    <Label className="text-xs text-red-600 font-semibold">Error Information</Label>
                    {selectedLog.error_code && (
                      <div className="mt-2">
                        <span className="text-xs text-red-500">Error Code: </span>
                        <span className="font-mono text-red-700">{selectedLog.error_code}</span>
                      </div>
                    )}
                    {selectedLog.error_message && (
                      <div className="mt-2 text-sm text-red-700">
                        {selectedLog.error_message}
                      </div>
                    )}
                    {selectedLog.error_details && (
                      <div className="mt-2">
                        <span className="text-xs text-red-500">Details: </span>
                        <pre className="mt-1 p-2 bg-red-100 rounded text-xs overflow-x-auto">
                          {JSON.stringify(selectedLog.error_details, null, 2)}
                        </pre>
                      </div>
                    )}
                    {selectedLog.stack_trace && (
                      <div className="mt-2">
                        <span className="text-xs text-red-500">Stack Trace: </span>
                        <pre className="mt-1 p-2 bg-red-100 rounded text-xs overflow-x-auto max-h-32">
                          {selectedLog.stack_trace}
                        </pre>
                      </div>
                    )}
                  </div>
                )}

                {selectedLog.external_id && (
                  <div>
                    <Label className="text-xs text-gray-500">External ID (Message SID)</Label>
                    <div className="mt-1 font-mono text-xs text-gray-600 break-all bg-gray-50 p-2 rounded">
                      {selectedLog.external_id}
                    </div>
                  </div>
                )}

                {selectedLog.template_used && (
                  <div>
                    <Label className="text-xs text-gray-500">Template Used</Label>
                    <div className="mt-1 font-mono text-xs text-gray-600">
                      {selectedLog.template_used}
                    </div>
                  </div>
                )}

                {/* Debug Information */}
                {(selectedLog.request_payload || selectedLog.response_payload) && (
                  <div className="border rounded-lg p-3 bg-slate-50">
                    <Label className="text-xs text-slate-600 font-semibold">Debug Information</Label>
                    
                    {selectedLog.request_payload && (
                      <div className="mt-2">
                        <span className="text-xs text-slate-500">Request Payload:</span>
                        <pre className="mt-1 p-2 bg-white border rounded text-xs overflow-x-auto max-h-40">
                          {JSON.stringify(selectedLog.request_payload, null, 2)}
                        </pre>
                      </div>
                    )}
                    
                    {selectedLog.response_payload && (
                      <div className="mt-2">
                        <span className="text-xs text-slate-500">Response Payload:</span>
                        <pre className="mt-1 p-2 bg-white border rounded text-xs overflow-x-auto max-h-40">
                          {JSON.stringify(selectedLog.response_payload, null, 2)}
                        </pre>
                      </div>
                    )}
                    
                    {selectedLog.duration_ms && (
                      <div className="mt-2 text-xs text-slate-500">
                        Duration: {selectedLog.duration_ms}ms
                      </div>
                    )}
                  </div>
                )}

                {/* Metadata */}
                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <div>
                    <Label className="text-xs text-gray-500">Additional Context</Label>
                    <pre className="mt-1 p-2 bg-gray-50 rounded text-xs overflow-x-auto">
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </pre>
                  </div>
                )}

                <div className="text-xs text-gray-400 pt-2 border-t">
                  <div>Log ID: {selectedLog.id}</div>
                  <div>Created: {formatDate(selectedLog.created_at)}</div>
                  {selectedLog.lead_id && <div>Lead ID: {selectedLog.lead_id}</div>}
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── Service History Payment Recipients Manager ── */
function ServiceHistoryRecipients() {
  const [recipients, setRecipients] = useState({ whatsapp_numbers: [], email_addresses: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newWhatsapp, setNewWhatsapp] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const getToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');

  useEffect(() => {
    axios.get(`${API_URL}/api/inspections/service-history-purchase/recipients`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    })
      .then(res => setRecipients(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [API_URL]);

  const saveRecipients = async (updated) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/inspections/service-history-purchase/recipients`, updated, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setRecipients(res.data);
      toast.success('Recipients updated');
    } catch {
      toast.error('Failed to save recipients');
    } finally {
      setSaving(false);
    }
  };

  const addWhatsapp = () => {
    const num = newWhatsapp.trim();
    if (!num) return;
    const formatted = num.startsWith('+') ? num : `+91${num.replace(/^0/, '')}`;
    if (recipients.whatsapp_numbers.includes(formatted)) {
      toast.error('Number already exists');
      return;
    }
    const updated = { ...recipients, whatsapp_numbers: [...recipients.whatsapp_numbers, formatted] };
    saveRecipients(updated);
    setNewWhatsapp('');
  };

  const removeWhatsapp = (num) => {
    const updated = { ...recipients, whatsapp_numbers: recipients.whatsapp_numbers.filter(n => n !== num) };
    saveRecipients(updated);
  };

  const addEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (recipients.email_addresses.includes(email)) {
      toast.error('Email already exists');
      return;
    }
    const updated = { ...recipients, email_addresses: [...recipients.email_addresses, email] };
    saveRecipients(updated);
    setNewEmail('');
  };

  const removeEmail = (email) => {
    const updated = { ...recipients, email_addresses: recipients.email_addresses.filter(e => e !== email) };
    saveRecipients(updated);
  };

  if (loading) return <div className="mt-3 text-xs text-gray-400">Loading recipients...</div>;

  return (
    <div className="mt-4 border-t pt-4" data-testid="service-history-recipients">
      <p className="text-xs font-semibold text-gray-700 mb-3">Notification Recipients</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* WhatsApp Numbers */}
        <div className="border rounded-lg p-3 bg-green-50/30">
          <div className="flex items-center gap-1.5 mb-2">
            <MessageSquare className="h-3.5 w-3.5 text-green-600" />
            <span className="text-xs font-medium text-gray-700">WhatsApp Numbers</span>
          </div>
          <div className="space-y-1.5 mb-2">
            {recipients.whatsapp_numbers.map(num => (
              <div key={num} className="flex items-center justify-between bg-white rounded px-2 py-1 border text-xs">
                <span className="text-gray-700 font-mono">{num}</span>
                <button onClick={() => removeWhatsapp(num)} className="text-red-400 hover:text-red-600 p-0.5" disabled={saving}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
            {recipients.whatsapp_numbers.length === 0 && (
              <p className="text-[10px] text-gray-400">No numbers configured</p>
            )}
          </div>
          <div className="flex gap-1.5">
            <input
              type="text"
              value={newWhatsapp}
              onChange={e => setNewWhatsapp(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addWhatsapp()}
              placeholder="e.g. 9611188788"
              className="flex-1 text-xs border rounded px-2 py-1 bg-white"
              data-testid="add-whatsapp-input"
            />
            <button
              onClick={addWhatsapp}
              disabled={saving || !newWhatsapp.trim()}
              className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
              data-testid="add-whatsapp-btn"
            >
              <Plus className="h-3 w-3" /> Add
            </button>
          </div>
        </div>

        {/* Email Addresses */}
        <div className="border rounded-lg p-3 bg-blue-50/30">
          <div className="flex items-center gap-1.5 mb-2">
            <Mail className="h-3.5 w-3.5 text-blue-600" />
            <span className="text-xs font-medium text-gray-700">Email Addresses</span>
          </div>
          <div className="space-y-1.5 mb-2">
            {recipients.email_addresses.map(email => (
              <div key={email} className="flex items-center justify-between bg-white rounded px-2 py-1 border text-xs">
                <span className="text-gray-700">{email}</span>
                <button onClick={() => removeEmail(email)} className="text-red-400 hover:text-red-600 p-0.5" disabled={saving}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
            {recipients.email_addresses.length === 0 && (
              <p className="text-[10px] text-gray-400">No emails configured</p>
            )}
          </div>
          <div className="flex gap-1.5">
            <input
              type="email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addEmail()}
              placeholder="e.g. name@wisedrive.in"
              className="flex-1 text-xs border rounded px-2 py-1 bg-white"
              data-testid="add-email-input"
            />
            <button
              onClick={addEmail}
              disabled={saving || !newEmail.trim()}
              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
              data-testid="add-email-btn"
            >
              <Plus className="h-3 w-3" /> Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CustomerCommunicationTab() {
  const [activeSubTab, setActiveSubTab] = useState('settings');
  const [settings, setSettings] = useState({});
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [emailConfig, setEmailConfig] = useState({ configured: false });
  const [showEmailConfig, setShowEmailConfig] = useState(false);
  const [emailFormData, setEmailFormData] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    from_name: 'WiseDrive'
  });
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  
  // Template editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTouchpoint, setEditingTouchpoint] = useState(null);
  const [editingChannel, setEditingChannel] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchEmailConfig();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/settings/communication`);
      const savedSettings = response.data?.settings || {};
      const savedTemplates = response.data?.templates || {};
      
      // Create default structure if not present
      const defaultSettings = {};
      const defaultTemplates = {};
      
      TOUCHPOINTS.forEach(tp => {
        defaultSettings[tp.id] = {};
        defaultTemplates[tp.id] = {};
        CHANNELS.forEach(ch => {
          // Default service_history_payment: WhatsApp + Email on, SMS off
          let defaultVal = false;
          if (tp.id === 'service_history_payment' && (ch.id === 'whatsapp' || ch.id === 'email')) {
            defaultVal = true;
          }
          defaultSettings[tp.id][ch.id] = savedSettings[tp.id]?.[ch.id] ?? defaultVal;
          defaultTemplates[tp.id][ch.id] = savedTemplates[tp.id]?.[ch.id] || DEFAULT_TEMPLATES[tp.id]?.[ch.id] || { message: '' };
        });
      });
      
      setSettings(defaultSettings);
      setTemplates(defaultTemplates);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to fetch communication settings:', error);
      // Initialize with defaults on error
      const defaultSettings = {};
      const defaultTemplates = {};
      TOUCHPOINTS.forEach(tp => {
        defaultSettings[tp.id] = {};
        defaultTemplates[tp.id] = {};
        CHANNELS.forEach(ch => {
          let defaultVal = false;
          if (tp.id === 'service_history_payment' && (ch.id === 'whatsapp' || ch.id === 'email')) {
            defaultVal = true;
          }
          defaultSettings[tp.id][ch.id] = defaultVal;
          defaultTemplates[tp.id][ch.id] = DEFAULT_TEMPLATES[tp.id]?.[ch.id] || { message: '' };
        });
      });
      setSettings(defaultSettings);
      setTemplates(defaultTemplates);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/email-config`);
      setEmailConfig(response.data || { configured: false });
      if (response.data?.smtp_user) {
        setEmailFormData(prev => ({
          ...prev,
          smtp_user: response.data.smtp_user
        }));
      }
    } catch (error) {
      console.error('Failed to fetch email config:', error);
    }
  };

  const saveEmailConfig = async () => {
    if (!emailFormData.smtp_user || !emailFormData.smtp_password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSavingEmail(true);
    try {
      await axios.post(`${API_URL}/settings/email-config`, emailFormData);
      toast.success('Email configuration saved!');
      await fetchEmailConfig();
      setShowEmailConfig(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email config');
    } finally {
      setSavingEmail(false);
    }
  };

  const testEmailConfig = async () => {
    setTestingEmail(true);
    try {
      const response = await axios.post(`${API_URL}/settings/email-config/test`);
      toast.success(response.data.message || 'Test email sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const toggleSetting = (touchpointId, channelId) => {
    setSettings(prev => ({
      ...prev,
      [touchpointId]: {
        ...prev[touchpointId],
        [channelId]: !prev[touchpointId]?.[channelId]
      }
    }));
    setHasChanges(true);
  };

  const openTemplateEditor = (touchpointId, channelId) => {
    setEditingTouchpoint(touchpointId);
    setEditingChannel(channelId);
    setEditorOpen(true);
  };

  const saveTemplate = async (newTemplate) => {
    setTemplates(prev => ({
      ...prev,
      [editingTouchpoint]: {
        ...prev[editingTouchpoint],
        [editingChannel]: newTemplate
      }
    }));
    setHasChanges(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/settings/communication`, { settings, templates });
      toast.success('Communication settings saved successfully!');
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save communication settings:', error);
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (presetName) => {
    const newSettings = {};
    TOUCHPOINTS.forEach(tp => {
      newSettings[tp.id] = {};
      CHANNELS.forEach(ch => {
        if (presetName === 'whatsapp_only') {
          newSettings[tp.id][ch.id] = ch.id === 'whatsapp';
        } else if (presetName === 'whatsapp_sms') {
          newSettings[tp.id][ch.id] = ch.id !== 'email';
        } else if (presetName === 'enable_all') {
          newSettings[tp.id][ch.id] = true;
        } else if (presetName === 'disable_all') {
          newSettings[tp.id][ch.id] = false;
        } else if (presetName === 'critical_only') {
          const isCritical = ['payment_link', 'payment_confirmation', 'report_otp', 'service_history_payment'].includes(tp.id);
          if (ch.id === 'whatsapp') newSettings[tp.id][ch.id] = isCritical;
          else if (ch.id === 'sms') newSettings[tp.id][ch.id] = tp.id === 'report_otp';
          else newSettings[tp.id][ch.id] = false;
        }
      });
    });
    setSettings(newSettings);
    setHasChanges(true);
    toast.info(`${presetName.replace(/_/g, ' ')} preset applied`);
  };

  const getActiveCount = (touchpointId) => {
    return CHANNELS.filter(ch => settings[touchpointId]?.[ch.id]).length;
  };

  const getChannelActiveCount = (channelId) => {
    return TOUCHPOINTS.filter(tp => settings[tp.id]?.[channelId]).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading communication settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-communication-tab">
      {/* Header with Sub-tabs */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Customer Communication</h2>
          <p className="text-sm text-gray-500">Configure touchpoints, channels, templates and view logs</p>
        </div>
      </div>

      {/* Sub-tabs: Settings vs Logs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Settings & Templates
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Communication Logs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="settings">
          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 mb-4">
            <Button variant="outline" onClick={fetchSettings} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              onClick={saveSettings} 
              disabled={saving || !hasChanges}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-1.5" />
              )}
              Save Changes
            </Button>
          </div>
      {!emailConfig.configured && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-medium text-yellow-900">Email Not Configured</h3>
              <p className="text-sm text-yellow-700 mt-1">
                Email notifications require SMTP configuration. Click below to set up Gmail SMTP.
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowEmailConfig(true)}
              className="flex-shrink-0"
            >
              <Mail className="h-4 w-4 mr-1.5" />
              Configure Email
            </Button>
          </div>
        </div>
      )}

      {/* Channel Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {CHANNELS.map(channel => {
          const Icon = channel.icon;
          const activeCount = getChannelActiveCount(channel.id);
          const isConfigured = channel.id !== 'email' || emailConfig.configured;
          
          return (
            <Card key={channel.id} className={`border-2 hover:border-gray-300 transition-colors ${!isConfigured ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-lg ${channel.bgColor} flex items-center justify-center`}>
                      <Icon className={`h-5 w-5 ${channel.color}`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 flex items-center gap-2">
                        {channel.name}
                        {!isConfigured && <Badge variant="outline" className="text-xs">Not Configured</Badge>}
                      </h3>
                      <p className="text-xs text-gray-500">{channel.description}</p>
                    </div>
                  </div>
                  <Badge variant={activeCount > 0 ? "default" : "secondary"} className="text-xs">
                    {activeCount}/{TOUCHPOINTS.length}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Communication Matrix with Templates */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Communication Matrix & Templates</CardTitle>
          <CardDescription>Toggle channels and edit message templates for each touchpoint</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {TOUCHPOINTS.map((touchpoint) => {
              const Icon = touchpoint.icon;
              const activeCount = getActiveCount(touchpoint.id);
              
              return (
                <div key={touchpoint.id} className="border rounded-lg p-4">
                  {/* Touchpoint Header */}
                  <div className="flex items-start gap-3 mb-4">
                    <div className={`h-10 w-10 rounded-lg ${touchpoint.bgColor} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`h-5 w-5 ${touchpoint.color}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{touchpoint.name}</span>
                        {activeCount > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {activeCount} channel{activeCount > 1 ? 's' : ''} active
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{touchpoint.description}</p>
                    </div>
                  </div>

                  {/* Channel Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {CHANNELS.map(channel => {
                      const ChannelIcon = channel.icon;
                      const isEnabled = settings[touchpoint.id]?.[channel.id];
                      const template = templates[touchpoint.id]?.[channel.id];
                      const isConfigured = channel.id !== 'email' || emailConfig.configured;
                      
                      return (
                        <div 
                          key={channel.id} 
                          className={`border rounded-lg p-3 ${isEnabled ? 'border-blue-200 bg-blue-50/50' : 'border-gray-200'} ${!isConfigured ? 'opacity-50' : ''}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <ChannelIcon className={`h-4 w-4 ${channel.color}`} />
                              <span className="text-sm font-medium">{channel.name}</span>
                            </div>
                            <Switch
                              checked={isEnabled || false}
                              onCheckedChange={() => toggleSetting(touchpoint.id, channel.id)}
                              disabled={!isConfigured}
                              data-testid={`toggle-${touchpoint.id}-${channel.id}`}
                            />
                          </div>
                          
                          {/* Template Preview */}
                          <div className="mt-2">
                            <div className="text-xs text-gray-500 line-clamp-2 mb-2 min-h-[2.5rem]">
                              {template?.message?.substring(0, 80) || 'No template configured'}
                              {template?.message?.length > 80 && '...'}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full text-xs h-7"
                              onClick={() => openTemplateEditor(touchpoint.id, channel.id)}
                            >
                              <Edit2 className="h-3 w-3 mr-1" />
                              Edit Template
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Recipient Management for Service History Payment */}
                  {touchpoint.id === 'service_history_payment' && (
                    <ServiceHistoryRecipients />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Presets */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Quick Presets</CardTitle>
          <CardDescription>Apply common configuration patterns</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => applyPreset('whatsapp_only')}>
              <MessageSquare className="h-4 w-4 mr-2 text-green-600" />
              WhatsApp Only
            </Button>
            <Button variant="outline" onClick={() => applyPreset('whatsapp_sms')}>
              <Phone className="h-4 w-4 mr-2 text-blue-600" />
              WhatsApp + SMS
            </Button>
            <Button variant="outline" onClick={() => applyPreset('enable_all')}>
              <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
              Enable All
            </Button>
            <Button variant="outline" onClick={() => applyPreset('disable_all')}>
              <X className="h-4 w-4 mr-2 text-red-600" />
              Disable All
            </Button>
            <Button variant="outline" onClick={() => applyPreset('critical_only')}>
              <AlertCircle className="h-4 w-4 mr-2 text-orange-600" />
              Critical Only
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Save Reminder */}
      {hasChanges && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50">
          <AlertCircle className="h-5 w-5" />
          <span className="font-medium">You have unsaved changes</span>
          <Button 
            size="sm" 
            variant="secondary" 
            onClick={saveSettings}
            disabled={saving}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Now'}
          </Button>
        </div>
      )}

      {/* Template Editor Modal */}
      <TemplateEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        touchpoint={editingTouchpoint}
        channel={editingChannel}
        template={templates[editingTouchpoint]?.[editingChannel]}
        onSave={saveTemplate}
      />

      {/* Email Configuration Modal */}
      <Dialog open={showEmailConfig} onOpenChange={setShowEmailConfig}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-purple-600" />
              Email Configuration (Gmail SMTP)
            </DialogTitle>
            <DialogDescription>
              Configure Gmail SMTP to send email notifications to customers
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <div className="font-medium text-blue-900 mb-1">How to get Gmail App Password:</div>
              <ol className="text-blue-700 space-y-1 list-decimal list-inside text-xs">
                <li>Go to your Google Account → Security</li>
                <li>Enable 2-Step Verification if not already enabled</li>
                <li>Go to "App passwords" under 2-Step Verification</li>
                <li>Generate a new app password for "Mail"</li>
                <li>Copy the 16-character password</li>
              </ol>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp_host">SMTP Host</Label>
                <Input
                  id="smtp_host"
                  value={emailFormData.smtp_host}
                  onChange={(e) => setEmailFormData(prev => ({ ...prev, smtp_host: e.target.value }))}
                  placeholder="smtp.gmail.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_port">SMTP Port</Label>
                <Input
                  id="smtp_port"
                  type="number"
                  value={emailFormData.smtp_port}
                  onChange={(e) => setEmailFormData(prev => ({ ...prev, smtp_port: parseInt(e.target.value) }))}
                  placeholder="587"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="smtp_user">Email Address *</Label>
              <Input
                id="smtp_user"
                type="email"
                value={emailFormData.smtp_user}
                onChange={(e) => setEmailFormData(prev => ({ ...prev, smtp_user: e.target.value }))}
                placeholder="notifications@wisedrive.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="smtp_password">App Password *</Label>
              <Input
                id="smtp_password"
                type="password"
                value={emailFormData.smtp_password}
                onChange={(e) => setEmailFormData(prev => ({ ...prev, smtp_password: e.target.value }))}
                placeholder="xxxx xxxx xxxx xxxx"
              />
              <p className="text-xs text-gray-500">Enter the 16-character app password (not your Gmail password)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="from_name">Sender Name</Label>
              <Input
                id="from_name"
                value={emailFormData.from_name}
                onChange={(e) => setEmailFormData(prev => ({ ...prev, from_name: e.target.value }))}
                placeholder="WiseDrive"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {emailConfig.configured && (
              <Button 
                variant="outline" 
                onClick={testEmailConfig}
                disabled={testingEmail}
                className="w-full sm:w-auto"
              >
                {testingEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Send Test Email
              </Button>
            )}
            <div className="flex gap-2 w-full sm:w-auto">
              <Button variant="outline" onClick={() => setShowEmailConfig(false)}>Cancel</Button>
              <Button onClick={saveEmailConfig} disabled={savingEmail}>
                {savingEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Configuration
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>

        {/* Communication Logs Tab */}
        <TabsContent value="logs">
          <CommunicationLogsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
